import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, CaretLeft, CaretRight, WifiHigh, WifiSlash, ArrowLeft, Funnel, X } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SignalBar({ value }) {
  if (value == null) return <span className="text-[var(--md-on-surface-variant)] text-xs">—</span>;
  const v = parseFloat(value);
  const color = v >= -25 ? '#1B5E20' : v >= -27 ? '#E65100' : '#B71C1C';
  const bg = v >= -25 ? '#E8F5E9' : v >= -27 ? '#FFF8E1' : '#FFEBEE';
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium tabular-nums" style={{ background: bg, color }}>
      {v.toFixed(1)} dBm
    </span>
  );
}

function StatusBadge({ status, enableuser }) {
  if (status === 'online') {
    return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#E8F5E9] text-[#1B5E20]"><WifiHigh size={10} weight="bold" /> Online</span>;
  }
  if (status === 'offline') {
    return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#FFEBEE] text-[#B71C1C]"><WifiSlash size={10} weight="bold" /> Offline</span>;
  }
  if (enableuser === 0 || enableuser === '0') {
    return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#F3E5F5] text-[#6A1B9A]">Disabled</span>;
  }
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#E3F2FD] text-[#0D47A1]">Unconfigured</span>;
}

export default function ONUListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState({ total: 0, page: 1, pages: 0, data: [] });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [odbFilter, setOdbFilter] = useState(searchParams.get('odb') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [odbOptions, setOdbOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');

  // Load ODB options for dropdown
  useEffect(() => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    fetch(`${API}/onu-list/odb-options`, {
      headers: token ? { Authorization: `Bearer ${token}` } : { credentials: 'include' }
    })
      .then(r => r.json())
      .then(setOdbOptions)
      .catch(() => {});
  }, []);

  // Sync URL param → filter state
  useEffect(() => {
    const odb = searchParams.get('odb') || '';
    setOdbFilter(odb);
    setPage(1);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const params = new URLSearchParams({ page, limit: 50 });
      if (odbFilter) params.append('odb', odbFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      const res = await fetch(`${API}/onu-list?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : { credentials: 'include' }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, odbFilter, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleOdbChange = (val) => {
    const newOdb = val === '__all__' ? '' : val;
    setOdbFilter(newOdb);
    setPage(1);
    if (newOdb) {
      setSearchParams({ odb: newOdb });
    } else {
      setSearchParams({});
    }
  };

  const clearOdbFilter = () => {
    setOdbFilter('');
    setPage(1);
    setSearchParams({});
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-[var(--md-surface-2)] transition-colors">
          <ArrowLeft size={20} className="text-[var(--md-on-surface-variant)]" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-[var(--md-on-surface)]">ONU List</h1>
          {odbFilter && (
            <p className="text-xs text-[var(--md-on-surface-variant)]">
              Filtered by ODB: <span className="font-medium text-[#E65100]">{odbFilter}</span>
            </p>
          )}
        </div>
        <div className="ml-auto text-xs text-[var(--md-on-surface-variant)] tabular-nums">
          {data.total.toLocaleString()} records
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search username, name, SN..."
            className="md3-input pl-9 h-10 w-full"
          />
        </form>

        {/* ODB Filter */}
        <div className="flex items-center gap-1">
          <Select value={odbFilter || '__all__'} onValueChange={handleOdbChange}>
            <SelectTrigger className="w-52 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10">
              <Funnel size={14} className="mr-1 text-[var(--md-on-surface-variant)]" />
              <SelectValue placeholder="All ODB Boxes" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-64 overflow-y-auto">
              <SelectItem value="__all__">All ODB Boxes</SelectItem>
              {odbOptions.map(o => (
                <SelectItem key={o.odb} value={o.odb}>
                  {o.odb} <span className="text-[var(--md-on-surface-variant)] ml-1">({o.count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {odbFilter && (
            <button onClick={clearOdbFilter} className="p-1.5 rounded-full hover:bg-[var(--md-surface-2)] transition-colors" title="Clear ODB filter">
              <X size={14} className="text-[var(--md-on-surface-variant)]" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="unconfigured">Unconfigured</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ODB Filter Banner */}
      {odbFilter && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFF8E1] border border-[#FFE082] text-sm">
          <span className="text-[20px]">📦</span>
          <span className="font-medium text-[#E65100]">{odbFilter}</span>
          <span className="text-[#5D4037]">— showing {data.total} connected ONU(s)</span>
          <button onClick={clearOdbFilter} className="ml-auto text-[10px] text-[#1565C0] hover:underline flex items-center gap-1">
            <X size={10} /> Clear filter
          </button>
        </div>
      )}

      {/* Table */}
      <div className="md3-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Status</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Name</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">SN / MAC</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">ONU</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden lg:table-cell">Zone</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden lg:table-cell">ODB</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Signal</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden xl:table-cell">Expiry</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-10">
                    <div className="w-6 h-6 border-2 border-[var(--md-primary)] border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : data.data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm text-[var(--md-on-surface-variant)]">
                    {odbFilter ? `No ONU records found for ODB: ${odbFilter}` : 'No ONU records found'}
                  </td>
                </tr>
              ) : (
                data.data.map((row, i) => (
                  <tr
                    key={i}
                    onClick={() => navigate(`/customers/${row.username}`)}
                    className="border-t border-[var(--md-outline-variant)] md3-table-row cursor-pointer hover:bg-[var(--md-surface-1)] transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <StatusBadge status={row.onu_status} enableuser={row.enableuser} />
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-medium text-[var(--md-primary)] tabular-nums">{row.username}</p>
                      <p className="text-[10px] text-[var(--md-on-surface-variant)]">{row.full_name?.trim() || '—'}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      {row.onu_serial ? (
                        <p className="text-[10px] font-mono tabular-nums text-[var(--md-on-surface)]">{row.onu_serial}</p>
                      ) : null}
                      {row.mac ? (
                        <p className="text-[10px] font-mono tabular-nums text-[var(--md-on-surface-variant)]">{row.mac}</p>
                      ) : null}
                      {!row.onu_serial && !row.mac && <span className="text-[10px] text-[var(--md-on-surface-variant)]">—</span>}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <span className="text-[10px] text-[var(--md-on-surface-variant)]">{row.service_name || '—'}</span>
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell">
                      <span className="text-[10px] text-[var(--md-on-surface-variant)]">{row.zone || '—'}</span>
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell">
                      {row.odb_splitter ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#FFF8E1] text-[#E65100]">
                          📦 {row.odb_splitter}
                        </span>
                      ) : <span className="text-[10px] text-[var(--md-on-surface-variant)]">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <SignalBar value={row.signal_level} />
                    </td>
                    <td className="px-4 py-2.5 hidden xl:table-cell">
                      <span className="text-[10px] text-[var(--md-on-surface-variant)]">{formatDate(row.expiration)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--md-outline-variant)]">
            <span className="text-xs text-[var(--md-on-surface-variant)]">
              Page {data.page} of {data.pages} · {data.total.toLocaleString()} total
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-full hover:bg-[var(--md-surface-2)] disabled:opacity-40 transition-colors"
              >
                <CaretLeft size={16} />
              </button>
              <span className="text-xs tabular-nums w-12 text-center">{page} / {data.pages}</span>
              <button
                onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                disabled={page >= data.pages}
                className="p-1.5 rounded-full hover:bg-[var(--md-surface-2)] disabled:opacity-40 transition-colors"
              >
                <CaretRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
